import React from 'react'
import { useTranslation } from 'react-i18next'
import { Text } from '@pandiafi/components'

import type { CoreParkingArea } from '../../../common/validations/contractParkingSpotsSchema'
import {
  spotDisplayId,
  formatDate,
  findActiveReservation,
  findFutureReservation,
} from './uiSelectors'
import { normDate } from './parkingSpots.helpers'

type ParkingReservationsListProps = {
  areas: CoreParkingArea[]
}

type Reservation = {
  spotId: string
  address: string
  startDate: string
  endDate: string | null
}

const extractOwnReservation = (
  reservation: ReturnType<typeof findActiveReservation>,
  spotId: string,
  address: string
): Reservation | null => {
  if (reservation?.kind !== 'own') {
    return null
  }

  const startDate = normDate(reservation.startDate)
  const endDate = normDate(reservation.endDate)

  if (!startDate) {
    return null
  }

  return {
    spotId,
    address,
    startDate,
    endDate: endDate ?? null,
  }
}

export const ParkingReservationsList: React.FC<
  ParkingReservationsListProps
> = ({ areas }) => {
  const { t, i18n } = useTranslation(['parking', 'reservations'])
  const lang = i18n.language

  const reservations: Reservation[] = []

  areas.forEach((area) => {
    const address = `${area.location.address.streetName} ${area.location.address.streetNumber}`

    area.parkingSpots.forEach((spot) => {
      const spotId = spotDisplayId(spot)
      const active = findActiveReservation(spot)
      const future = findFutureReservation(spot)

      const ownReservation =
        extractOwnReservation(active, spotId, address) ??
        extractOwnReservation(future, spotId, address)

      if (ownReservation) {
        reservations.push(ownReservation)
      }
    })
  })

  if (reservations.length === 0) {
    return null
  }

  return (
    <div className="space-y-1 mt-8 lg:mt-10">
      <div>
        <Text element="div" size="l" weight="bold">
          {t('reservations:myReservations')}
        </Text>
      </div>
      <div>
        {reservations.map((reservation) => (
          <div
            key={`${reservation.spotId}-${reservation.startDate}`}
            className="mb-4"
          >
            <div>
              {reservation.spotId} - {reservation.address}
            </div>
            <div>
              {reservation.endDate
                ? `${formatDate(reservation.startDate, lang)} - ${formatDate(reservation.endDate, lang)}`
                : `${t('reservations:timeFormatting.startingFrom')} ${formatDate(reservation.startDate, lang)}`}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
